import React, { useState } from 'react';
import Button from '../../common/Button';
import { MemberDTO, FriendAndPartnerDTO } from '../../../services/apiClient';
import { useFriendAndPartners } from '../../../hooks/useFriendAndPartnerApi';
import ImagePreview from '../../common/ImagePreview';
import { Loader2 } from 'lucide-react';

interface Props {
  member: MemberDTO;
  onChange: (updated: Partial<MemberDTO>) => void;
  onNext: () => void;
  onBack: () => void;
}

const OptInServices: React.FC<Props> = ({ onChange, onNext, onBack }) => {
  const { data: partners, isLoading } = useFriendAndPartners();
  const [optedIn, setOptedIn] = useState<Record<string, boolean>>({});

  const optInPartners = [...(partners || [])]
    .map((p) => p as FriendAndPartnerDTO)
    .filter((p) => p.isActive && p.allowOptIn)
    .sort((a, b) => (a.displayPosition ?? 0) - (b.displayPosition ?? 0));

  const handleToggle = (partnerId: string) => {
    setOptedIn((prev) => ({ ...prev, [partnerId]: !prev[partnerId] }));
  };

  const handleNext = () => {
    const selectedIds = Object.entries(optedIn)
      .filter(([, v]) => v)
      .map(([id]) => id);
    onChange({ partnerOptInIds: selectedIds } as any);
    onNext();
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Partner Services</h2>
      <p className="text-sm text-gray-500 mb-6">
        Opt in to exclusive offers from our Friends &amp; Partners. All opt-ins are optional.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin h-6 w-6 text-gray-400" />
        </div>
      ) : optInPartners.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No partner services available at this time.</p>
      ) : (
        <div className="space-y-4">
          {optInPartners.map((partner) => (
            <label
              key={partner.id}
              className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                optedIn[partner.id!]
                  ? 'border-secondary bg-primary/5'
                  : 'border-gray-200 hover:border-primary/40 dark:border-gray-700'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-primary flex-shrink-0"
                checked={!!optedIn[partner.id!]}
                onChange={() => handleToggle(partner.id!)}
              />

              <div className="flex-1 text-left">
                {partner.imageFileRecordId && (
                  <div className="mb-2">
                    <ImagePreview fileId={partner.imageFileRecordId} size="md" />
                  </div>
                )}
                <p className="text-sm font-semibold text-primary dark:text-gray-200 mb-1">
                  {partner.name}
                </p>
                {partner.optInDescription && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {partner.optInDescription}
                  </p>
                )}
                {partner.optInUrl && (
                  <a
                    href={partner.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-secondary hover:underline mt-1 inline-block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Learn more
                  </a>
                )}
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="mt-6 flex justify-between w-full">
        <Button onClick={onBack} variant="secondary">
          Back
        </Button>
        <Button onClick={handleNext} variant="primary">
          Next
        </Button>
      </div>
    </div>
  );
};

export default OptInServices;
